import { formatMoney, formatQuantity, t } from "@hakili/i18n";
import { AlertTriangle, ArrowLeft } from "lucide-react";

import { useState } from "react";

import {
  ApportOrigine,
  type ApportBody,
  type ApportDeclaration,
  apportDeclarationBody,
  emptyApportDeclaration,
} from "./apport-origine.js";
import type { ProfileBasis } from "./profile-basis.js";

/**
 * `round CW`, `CW.4`: **« Vérifier mon éligibilité », `WF-CLI-05` and `06`.**
 *
 * The form, the capacity, and the two issues , favourable and not , each with
 * the sentences the workflows name.
 *
 * ## Not one figure is computed here
 *
 * The capacity, the effort, the band, the shortfall and the horizon all come
 * from `POST /api/v1/prequalifications`. This screen renders an answer. That is
 * the rule the whole product turns on: *"divergence between the teaser and the
 * real offer is the single worst product failure"* (`01`), and a screen that
 * recomputed any of it would be a second opinion nobody asked for.
 *
 * ## The bar never renders without a salary
 *
 * `WF-CLI-05`'s own acceptance criterion, and the failure it names is concrete:
 * a percentage over a salary of zero is `Infinity`, and a bar drawn from it is a
 * screen telling a person something about their money that is not a number.
 */

export type Band = "confortable" | "acceptable" | "au-dela";

export interface FavourableIssue {
  readonly eligible: true;
  readonly capacityAmount: number;
  readonly instalmentAmount: number | null;
  readonly effortPct: number | null;
  /** `C3-A.2`: this instalment alone , the drawing's first figure. */
  readonly instalmentPct: number | null;
  readonly band: Band | null;
  readonly resteAVivreAmount: number | null;
  readonly notice: string;
}

export interface UnfavourableIssue {
  readonly eligible: false;
  readonly capacityAmount: number;
  readonly instalmentAmount: number | null;
  readonly shortfallAmount: number;
  readonly netSalaryAmount: number;
  readonly existingLoanInstalmentAmount: number;
  readonly effortPct: number | null;
  /** `C3-A.2`: this instalment alone , the drawing's first figure. */
  readonly instalmentPct: number | null;
  readonly band: Band | null;
  readonly whatWouldChange: readonly string[];
  readonly capacityWatch: { readonly offered: true; readonly horizonMonths: number | null };
  readonly notice: string;
}

export type Issue = FavourableIssue | UnfavourableIssue;

/**
 * `round C2`, `C2.1`: **`WF-CLI-05` step 1's own three situations.**
 *
 * The founder walked this screen and met « La situation doit être précisée » ,
 * which is the DOOR's refusal, thrown when `employment` is not one of `02`
 * 2.6.3's three. The screen never asked, and `App` sent the literal
 * `"salarie"`, which is none of them: **the eligibility form could not succeed
 * for anybody.**
 *
 * `C2.9`: the three moved to `@hakili/domain-types`, where the door's own
 * vocabulary belongs and where an accented WIRE VALUE is not a label a
 * catalogue may re-word. Re-exported here under the name this screen has always
 * used, so the pair-maker and the corridor keep one word for one thing.
 */
/**
 * `C3.0(f)`: the drawing's five next steps, in its own order.
 *
 * Keys and not sentences (`06` §6.5.1), and **the expert's step carries no
 * date**: the drawing writes `PROCHAIN_CONTROLE`, a constant chosen by hand,
 * and the door that derives it is `round C3-B`'s.
 */
const SUITE = [
  "client.eligibilite.suiteFrais",
  "client.eligibilite.suiteExpert",
  "client.eligibilite.suiteDecider",
  "client.eligibilite.suitePieces",
  "client.eligibilite.suiteBanques",
] as const;

/**
 * `round N+3`, `S2-14`: `GET /me/prequalification`'s `declared` block , the
 * client's own last answer to this form, with the instant it was given.
 *
 * **No `bankName` field exists here, and none may be added.** The door this
 * is read from has no column to read one from (`PrequalificationsController`
 * 's own comment), so a pre-fill built off this type cannot leak one even by
 * a careless spread , the shape itself refuses it.
 */
export interface DeclaredLatest {
  readonly netSalaryAmount: number;
  readonly employment: string;
  readonly existingLoanInstalmentAmount: number;
  readonly declaredAt: string;
  /**
   * Whether this declaration is still younger than the door's own freshness
   * window , decided server side, with an injected `Clock`, and read here
   * rather than recomputed: `9.2.4`'s `D2` bans `Date.now()` and a bare
   * `new Date()` everywhere outside `platform/src/clock`, this screen
   * included, and this screen has no `Clock` to inject.
   */
  readonly fresh: boolean;
}

export interface EligibilityProps {
  /**
   * `round C3`, `C3.0(f)`: **the financing the SIMULATION door answered**, carried
   * here and never recomputed.
   *
   * The drawing's favourable card opens with *« vous pouvez viser un
   * financement d'environ X FCFA sur N mois »* and prints the estimated
   * instalment above the bar. All three are the fiche's own quote , the same
   * figures `POST /api/v1/simulation/quote` gave and the same `instalmentAmount`
   * this screen then sent to the prequalification. **No figure on this screen is
   * computed by this screen** (`WF-CLI-05`, and `01`'s rule that a divergence
   * between the teaser and the real offer is the worst failure this product
   * has).
   *
   * **Absent in `standalone` mode**, `round N+3`'s `S2-14`: there is no
   * vehicle there to carry a quote about, and that mode's `onSubmit` never
   * produces a favourable `issue` either, so the branch that reads this is
   * unreachable when it is missing.
   */
  readonly financing?:
    | {
        readonly amountFinanced: number;
        readonly durationMonths: number;
        readonly monthlyAmount: number;
        /** `OD-37` round 7: the deposit the file will carry, whole percent; above zero, its origin is asked. */
        readonly depositPct?: number;
      }
    | undefined;
  /**
   * What the buyer DECLARED, echoed back so the bar can take shares of it.
   *
   * The favourable issue does not carry the salary , it has no reason to , and
   * the bar's three segments are shares of exactly that. It is his own answer
   * and not a figure anybody computed, which is why it travels as a declaration
   * rather than as a result.
   */
  readonly declared: {
    readonly netSalaryAmount: number;
    readonly existingLoanInstalmentAmount: number;
  };
  /** `null` until the buyer has answered , the form is the first state. */
  readonly issue: Issue | null;
  readonly refusal: string | null;
  readonly busy: boolean;
  /**
   * `REG-PROFILE-001`: **the profile the check will read**, as the API derives it, or `null` while it has not
   * answered. The screen shows it and computes none of it.
   */
  readonly basis: ProfileBasis | null;
  /** Runs the check. No input: `POST /api/v1/prequalifications` reads the declared profile itself. */
  readonly onSubmit: () => void;
  /** The way to the profile editor, from the summary and from both guards. */
  readonly onEditProfile: () => void;
  /** `OD-37` round 7: the declaration of the deposit's origin, handed to the door with the opening. */
  readonly onOpenDossier: (declaration: ApportBody) => void;
  /** `round W-13`, `M2`: a buyer's hold stands on the car , « Ouvrir mon dossier » greyed with the reason. */
  readonly engaged?: boolean;
  /** `C3-A.2`: the drawing's arrow, back to the form he filled. */
  readonly onBack: () => void;
  readonly onSeeAffordable: () => void;
  readonly onBuyCash: () => void;
}

/**
 * What would change the situation, keyed by the WIRE VALUE.
 *
 * `whatWouldChange` is `simulation`'s own vocabulary, so it is DATA and never a
 * name (`09-01`'s first answer): the map must match the wire, and building the
 * key with a template would also hide it from the catalogue's own guard , which
 * requires three parts and can only check keys it can see.
 */
const CHANGES = new Map<string, string>([
  ["vehiculeMoinsCher", "client.eligibilite.changeVehicule"],
  ["apportPlusEleve", "client.eligibilite.changeApport"],
  ["finDuPretEnCours", "client.eligibilite.changeFinDuPret"],
]);

/** The three bands, in the record's own words, with the colour each carries. */
const BANDS: Readonly<Record<Band, { readonly key: string; readonly tone: string; readonly fond: string }>> =
  {
    // `C3.0(f)`: the drawing gives each band a GROUND as well as a text colour ,
    // the tone pattern of 04 §4.6.3, a `-50` ground under a `-700` text.
    confortable: {
      key: "client.eligibilite.bandeConfortable",
      tone: "text-emerald-700",
      fond: "bg-emerald-50",
    },
    acceptable: { key: "client.eligibilite.bandeAcceptable", tone: "text-amber-700", fond: "bg-amber-50" },
    "au-dela": { key: "client.eligibilite.bandeAuDela", tone: "text-rose-700", fond: "bg-rose-50" },
  };

/**
 * The capacity bar.
 *
 * **It refuses to render without a salary**, and the refusal is the point: the
 * effort is a percentage of what a person earns, and a percentage of nothing is
 * `Infinity`. A bar drawn from that would be a picture of a number nobody
 * computed.
 */
/**
 * `round C3`, `C3.0(f)`: **the drawing's bar, in its three segments.**
 *
 * It drew ONE bar of the effort percentage against a grey track. The drawing
 * divides a person's salary into what he already owes, what this car would
 * cost him, and what he would have left , and it marks 50 %, *« la limite
 * au-delà de laquelle les banques ne prêtent pas »*. A single bar cannot say
 * that: it shows a number, where the drawing shows a month.
 *
 * **Every FIGURE is the door's** , `effortPct` and `resteAVivreAmount` come
 * from the prequalification, the instalment from the simulation, the salary and
 * the loan are what the buyer himself declared. The widths are geometry over
 * those figures and not money: `9.2.3 M3` puts rounding at the point of
 * computation, and a percentage of a bar's length is rounded by the browser's
 * layout, never by this file.
 *
 * **It refuses to render without a salary**, and the refusal is the point: the
 * segments are shares of what a person earns, and a share of nothing is
 * `Infinity`.
 */
export function CapacityBar({
  effortPct,
  instalmentPct,
  band,
  netSalaryAmount,
  existingLoanInstalmentAmount,
  instalmentAmount,
  resteAVivreAmount,
}: {
  readonly effortPct: number | null;
  /** `C3-A.2`: this instalment alone , the drawing's first figure. */
  readonly instalmentPct: number | null;
  readonly band: Band | null;
  readonly netSalaryAmount: number;
  readonly existingLoanInstalmentAmount: number;
  readonly instalmentAmount: number | null;
  readonly resteAVivreAmount: number | null;
}): React.JSX.Element | null {
  if (effortPct === null || instalmentPct === null || band === null || netSalaryAmount <= 0) return null;
  const share = (amount: number): string => `${String(Math.max(0, (amount / netSalaryAmount) * 100))}%`;
  const instalment = instalmentAmount ?? 0;
  return (
    <div data-testid="eligibilite-barre" className="mt-4">
      {resteAVivreAmount === null ? null : (
        <>
          <p className="text-xs text-gray-500">{t("client.eligibilite.resteApres")}</p>
          <p className="mt-0.5 text-xl font-semibold tabular-nums" data-testid="eligibilite-reste">
            {formatMoney(resteAVivreAmount)} {t("client.eligibilite.parMois")}
          </p>
        </>
      )}

      <div className="relative mt-3">
        <div className="flex h-4 overflow-hidden rounded-full bg-gray-100">
          {existingLoanInstalmentAmount > 0 ? (
            <div
              className="h-full bg-gray-400"
              style={{ width: share(existingLoanInstalmentAmount) }}
              data-testid="eligibilite-segment-pret"
            />
          ) : null}
          <div
            className="h-full bg-indigo-600"
            style={{ width: share(instalment) }}
            data-testid="eligibilite-segment-mensualite"
          />
          <div
            // `C3-A.2`: the DRAWING's green. `emerald-700` stood here because
            // `C2` cured this bar from `emerald-400`, a shade the closed
            // palette did not carry and which therefore rendered nothing at
            // all , the nearest shade it held was reached for. The palette now
            // carries `400`, so the bar is the weight the founder was shown.
            className="h-full bg-emerald-400"
            style={{ width: share(resteAVivreAmount ?? 0) }}
            data-testid="eligibilite-segment-reste"
          />
        </div>
        {/* The drawing's own mark, at the ceiling the banks hold. */}
        <div
          className="absolute top-0 h-4 border-l-2 border-gray-900"
          style={{ left: "50%" }}
          data-testid="eligibilite-marque-50"
        />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3">
        {existingLoanInstalmentAmount > 0 ? (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <span className="h-2 w-2 rounded-full bg-gray-400" />
            {t("client.eligibilite.legendePret")}
          </span>
        ) : null}
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <span className="h-2 w-2 rounded-full bg-indigo-600" />
          {t("client.eligibilite.legendeMensualite")}
        </span>
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <span className="h-2 w-2 rounded-full bg-emerald-400" />
          {t("client.eligibilite.legendeReste")}
        </span>
      </div>
      <p className="mt-1.5 text-xs text-gray-400">{t("client.eligibilite.marqueCinquante")}</p>

      <div className={`mt-3 rounded-xl px-4 py-3 ${BANDS[band].fond}`}>
        <p className={`text-sm font-medium ${BANDS[band].tone}`}>{t(BANDS[band].key)}</p>
        <p className="mt-1 text-xs leading-relaxed text-gray-500">
          {/**
           * `round C3-A`, `C3-A.2`: **the drawing's TWO figures.**
           *
           * This printed one , « Vos charges représentent 19 % » , and the
           * drawing prints « Cette mensualité représente X % de votre salaire,
           * et Y % avec votre prêt en cours. » The difference is not decoration:
           * the single figure is the effort WITH the running loan folded in, so
           * a buyer carrying one could not tell what this car costs him from
           * what he already owes, which is exactly the question the screen is
           * open to answer.
           *
           * The second clause appears only when a loan was declared, as the
           * drawing has it: with none, the two figures are the same number and
           * printing it twice would be noise.
           *
           * Printed as the door sent them. `9.2.3 M3`: rounding happens ONCE,
           * at the point of computation , `simulation` owns these figures, and
           * a second rounding here is how two screens come to disagree about
           * one person's effort.
           */}
          {t("client.eligibilite.effortMensualite")} {formatQuantity(instalmentPct)}{" "}
          {existingLoanInstalmentAmount > 0
            ? `${t("client.eligibilite.effortDuSalaire")}${t("client.eligibilite.effortEt")} ${formatQuantity(effortPct)} ${t("client.eligibilite.effortAvecPret")}`
            : t("client.eligibilite.effortSeul")}
        </p>
      </div>
    </div>
  );
}

/**
 * `OD-37`, round 7: **the opening step.** « Ouvrir mon dossier » is the gesture that births the file, so it is also
 * where the buyer says where his deposit is: the button stays shut until the declaration is complete, and hands it
 * to the door with the opening. Its own component, because the declaration is state and `Eligibility` returns early.
 */
function OpenTheFile(props: {
  readonly depositPct: number;
  readonly busy: boolean;
  readonly engaged: boolean;
  readonly onOpen: (declaration: ApportBody) => void;
}): React.JSX.Element {
  const [apport, setApport] = useState<ApportDeclaration>(emptyApportDeclaration());
  const declaration = apportDeclarationBody(props.depositPct, apport);
  return (
    <>
      <ApportOrigine depositPct={props.depositPct} value={apport} onChange={setApport} />
      <button
        type="button"
        data-testid="eligibilite-ouvrir"
        disabled={props.busy || props.engaged || declaration === null}
        title={props.engaged ? t("client.vehicule.engage") : undefined}
        onClick={() => {
          if (declaration !== null) props.onOpen(declaration);
        }}
        className="w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-40"
      >
        {t("client.eligibilite.ouvrir")}
      </button>
    </>
  );
}

export function Eligibility(props: EligibilityProps): React.JSX.Element {
  if (props.issue === null) return <ProfileSummary {...props} />;

  const issue = props.issue;
  const declaredLoan = issue.eligible ? 0 : issue.existingLoanInstalmentAmount;
  const declaredSalary = issue.eligible ? 0 : issue.netSalaryAmount;
  // `round N+3`, `S2-14`: `financing` is absent in `standalone` mode, which
  // never reaches a favourable issue , the caller never sets one, exactly
  // because there is no vehicle here to carry one about. The fallback below
  // is therefore dead weight kept for the type, not a figure this screen can
  // ever actually show.
  const financing = props.financing ?? { amountFinanced: 0, durationMonths: 0, monthlyAmount: 0 };
  return (
    <div className="flex flex-col gap-3">
      {/**
       * `round C3-A`, `C3-A.2`: **the drawing's own header, and its way back.**
       *
       * This printed « Vous pouvez emprunter » or « Pas encore, et voici
       * pourquoi » as the heading, and the drawing puts « Résultat » there with
       * an arrow back to the form , the verdict itself being the card's own
       * first line, where the drawing says it once. Two consequences, and the
       * second is the one that matters: a buyer who mistyped a salary had NO
       * WAY BACK to the form except the browser, and this app is a handset.
       */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="eligibilite-retour"
          aria-label={t("client.eligibilite.retourFormulaire")}
          onClick={props.onBack}
          className="text-gray-500"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="text-lg font-semibold tracking-tight text-gray-900">
          {t("client.eligibilite.resultat")}
        </h1>
      </div>

      {issue.eligible ? (
        <>
          {/**
           * `round C3`, `C3.0(f)`: **the drawing's favourable card**, which
           * `P1-CLW-005`'s clause has been open for since `CW`.
           *
           * It showed the capacity as a bare figure under a heading. The drawing
           * opens with what the buyer can actually DO , a financing of about so
           * much over so many months , then the instalment in large type, then
           * the bar. Every figure is a door's: the three above the bar are the
           * fiche's own quote, carried in as {@link EligibilityProps.financing},
           * and the rest is the prequalification's answer.
           */}
          <section
            data-testid="eligibilite-capacite"
            className="rounded-2xl border border-emerald-200 bg-white px-5 py-5"
          >
            <p className="text-base font-semibold text-gray-900">{t("client.eligibilite.profilEligible")}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-500" data-testid="eligibilite-viser">
              {t("client.eligibilite.viser")} {formatMoney(financing.amountFinanced)}{" "}
              {t("client.eligibilite.sur")} {String(financing.durationMonths)} {t("client.vehicule.mois")}
            </p>

            <div className="mt-4 rounded-xl bg-indigo-50 px-4 py-3">
              <p className="text-xs text-gray-500">{t("client.eligibilite.mensualiteEstimee")}</p>
              <p className="mt-0.5 text-2xl font-semibold tabular-nums text-indigo-700">
                {formatMoney(financing.monthlyAmount)}
              </p>
            </div>

            <CapacityBar
              effortPct={issue.effortPct}
              instalmentPct={issue.instalmentPct}
              band={issue.band}
              netSalaryAmount={props.declared.netSalaryAmount}
              existingLoanInstalmentAmount={props.declared.existingLoanInstalmentAmount}
              instalmentAmount={issue.instalmentAmount ?? financing.monthlyAmount}
              resteAVivreAmount={issue.resteAVivreAmount}
            />
          </section>

          {/* The drawing's amber caveat: an estimate is not a bank's answer. */}
          <section
            data-testid="eligibilite-estimation"
            className="flex items-start gap-2.5 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3.5"
          >
            {/* `C3-A.2`: the drawing's own notice , a light border and the
                warning triangle beside the sentence. The build had `amber-700`
                and no icon, because the palette carried neither `amber-100` nor
                `amber-500` and there was no shade to draw one in. */}
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <p className="text-xs leading-relaxed text-gray-500">
              {t("client.eligibilite.estimation")}{" "}
              {/* `C3-B.1`: the one estimate, named , the caveat's own type. */}
              <span data-testid="eligibilite-estimation-frais">
                {t("client.eligibilite.estimationFrais")}
              </span>
            </p>
          </section>

          {/**
           * The drawing's five steps. **The expert's own step carries no DATE**:
           * the next control day is a door of `expertise` that `round C3-B`
           * builds, and the drawing's `PROCHAIN_CONTROLE` is a constant chosen
           * by hand in a bundle. A dash where the date will go, never a delay
           * and never a guess , the architect's ruling on the split.
           */}
          <section
            data-testid="eligibilite-suite"
            className="rounded-2xl border border-gray-200 bg-white px-4 py-4"
          >
            <p className="mb-2 text-sm font-medium text-gray-900">{t("client.eligibilite.laSuite")}</p>
            {SUITE.map((key, index) => (
              <div key={key} className="flex items-start gap-2.5 py-1.5">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-700">
                  {String(index + 1)}
                </span>
                <span className="text-sm leading-snug text-gray-500">{t(key)}</span>
              </div>
            ))}
          </section>

          {/**
           * `round C3-A`, `C3-A.0(c)`: **the refusal had nowhere to land, and
           * that is the SECOND half of the founder's finding.**
           *
           * `props.refusal` was rendered inside the FORM branch alone. The
           * result screen carries the control that opens a file , the one
           * gesture on this screen that can be refused by a door , and had no
           * place at all to print the refusal. So a door answering « ce
           * véhicule n'est plus disponible » would have been caught, stored in
           * state, and shown to nobody.
           *
           * It only became visible once the first half was cured: while the
           * control called no door, there was never a refusal to swallow. **A
           * gate may close, never in silence.**
           */}
          {props.refusal === null ? null : (
            <p role="alert" data-testid="eligibilite-refus-ouvrir" className="text-sm text-rose-600">
              {props.refusal}
            </p>
          )}

          <OpenTheFile
            depositPct={props.financing?.depositPct ?? 0}
            busy={props.busy}
            engaged={props.engaged === true}
            onOpen={props.onOpenDossier}
          />
        </>
      ) : (
        <>
          {/**
           * `C3.0(f)`: the drawing's unfavourable card , **one sentence carrying
           * the three figures the buyer declared and the door computed**, where
           * the build printed a capacity and a separate « Il manque ».
           */}
          <section
            data-testid="eligibilite-capacite"
            className="rounded-2xl border border-gray-200 bg-white px-5 py-5"
          >
            <p className="text-base font-semibold text-gray-900">{t("client.eligibilite.defavorable")}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-500" data-testid="eligibilite-manque">
              {t("client.eligibilite.avecUnSalaire")} {formatMoney(declaredSalary)}
              {declaredLoan > 0 ? `${t("client.eligibilite.etUnPret")} ${formatMoney(declaredLoan)}` : ""}
              {t("client.eligibilite.capaciteEstDe")} {formatMoney(issue.capacityAmount)}
              {t("client.eligibilite.ilManque")} {formatMoney(issue.shortfallAmount)}
              {t("client.eligibilite.pourCeVehicule")}
            </p>
          </section>

          <section
            data-testid="eligibilite-changerait"
            className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5"
          >
            <p className="text-sm font-semibold text-gray-900">{t("client.eligibilite.changerait")}</p>
            <ul className="mt-2 list-disc pl-4 text-sm text-gray-500">
              {/* THREE lines at most, and the third only when a loan exists ,
                  the DOOR decides which, and this renders what it sent. A
                  screen that always showed all three would tell a buyer with no
                  loan to wait for one to end. */}
              {issue.whatWouldChange.map((what) => {
                const key = CHANGES.get(what);
                return key === undefined ? null : <li key={what}>{t(key)}</li>;
              })}
            </ul>
            {issue.capacityWatch.horizonMonths === null ? null : (
              <p className="mt-2 text-xs text-gray-500" data-testid="eligibilite-veille">
                {t("client.eligibilite.veille")} {String(issue.capacityWatch.horizonMonths)}{" "}
                {t("client.vehicule.mois")}
              </p>
            )}
          </section>

          <button
            type="button"
            data-testid="eligibilite-dans-ma-capacite"
            onClick={props.onSeeAffordable}
            className="w-full rounded-2xl border border-indigo-200 bg-white py-3.5 text-sm font-medium text-indigo-600"
          >
            {t("client.eligibilite.dansMaCapacite")}
          </button>
          <button
            type="button"
            data-testid="eligibilite-comptant"
            onClick={props.onBuyCash}
            className="w-full rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-500"
          >
            {t("client.eligibilite.comptant")}
          </button>
        </>
      )}

      {/**
       * `round C3-A`, `C3-A.0(c)`: **the disclaimer was printed TWICE**, and
       * the founder read both on staging , the amber card, then this line in
       * grey beneath the control.
       *
       * Two sources for one promise: `client.eligibilite.estimation` in the
       * card above, and `issue.notice` here, the door's own sentence. The
       * DRAWING decides, and the drawing carries it once, in the amber card,
       * with exactly the catalogue's two sentences and nothing beneath its
       * « Ouvrir mon dossier » at all. So this line goes.
       *
       * **Nothing is weakened by its going**, which is what the comment that
       * stood here was defending: the door's sentence , *"Ceci est une
       * estimation fondée sur vos déclarations, pas un accord de la banque."* ,
       * is the catalogue text's FIRST SENTENCE, verbatim, so the promise the
       * bank's answer later has to live up to is still on the screen and still
       * in the door's words. What is gone is saying it to a buyer twice.
       */}
    </div>
  );
}

/** The preteurs joined the way a sentence reads: « A », « A et B », « A, B et C ». */
const naming = (names: readonly string[]): string =>
  names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} et ${names[names.length - 1] ?? ""}`;

/**
 * `REG-PROFILE-001`, Arc B: **what the form step became.**
 *
 * `WF-CLI-05` step 1 asked three questions on every check. Step 0 of `docs/claude/underwriter-workflow.md` has the
 * check read the declared profile instead, so this is a read-only summary of what it will read, with a way to the
 * editor, and two guards in front of the verdict. **The summary only; the result screens never name a lender.**
 *
 * - Guard 1: no employment, or the commitments question never answered: routed to complete the profile. The
 *   screen draws no salary for it, because « 0 FCFA » would read as a fact about the client's income.
 * - Guard 2: a commitment balance whose own date is past the API's threshold: routed to refresh it. The
 *   threshold printed is the one the API sent in `basis.staleAfterDays`; this file owns no number.
 */
function ProfileSummary(props: EligibilityProps): React.JSX.Element {
  const basis = props.basis;
  if (basis === null) {
    return (
      <p data-testid="eligibilite-chargement" role="status" className="text-sm text-gray-500">
        {t("client.eligibilite.chargement")}
      </p>
    );
  }
  const noEmployment = basis.blockers.includes("no_employment");
  const undeclared = basis.blockers.includes("commitments_undeclared");
  const stale = basis.blockers.includes("commitments_stale");
  const staleLenders = naming(
    basis.commitments.filter((one) => basis.staleCommitmentIds.includes(one.id)).map((one) => one.lenderName),
  );

  return (
    <div className="flex flex-col gap-3" data-testid="eligibilite-resume">
      <h1 className="text-xl font-semibold tracking-tight text-gray-900">{t("client.eligibilite.titre")}</h1>
      <p className="text-sm text-gray-500">{t("client.eligibilite.sousProfil")}</p>

      {noEmployment ? (
        <p
          data-testid="eligibilite-profil-incomplet"
          role="status"
          className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700"
        >
          {t("client.eligibilite.profilIncomplet")}
        </p>
      ) : (
        <section className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
          <p className="text-xs text-gray-500">{t("client.eligibilite.resumeSalaire")}</p>
          <p className="mt-0.5 text-xl font-semibold tabular-nums" data-testid="eligibilite-resume-salaire">
            {formatMoney(basis.netSalaryAmount)}
          </p>
          <p className="mt-3 text-xs text-gray-500">{t("client.eligibilite.resumeEmplois")}</p>
          <ul className="mt-1 text-sm text-gray-900" data-testid="eligibilite-resume-emplois">
            {basis.employments.map((one) => (
              <li key={one.id}>
                {one.employer}, {one.position}
              </li>
            ))}
          </ul>
        </section>
      )}

      {undeclared ? (
        <p
          data-testid="eligibilite-engagements-a-declarer"
          role="status"
          className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700"
        >
          {t("client.eligibilite.engagementsADeclarer")}
        </p>
      ) : noEmployment ? null : basis.noCommitmentsDeclared || basis.commitments.length === 0 ? (
        <p data-testid="eligibilite-resume-aucun-engagement" className="text-sm text-gray-500">
          {t("client.eligibilite.resumeAucun")}
        </p>
      ) : (
        <section className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
          <p className="text-xs text-gray-500">{t("client.eligibilite.resumeEngagements")}</p>
          <ul className="mt-1 text-sm text-gray-900" data-testid="eligibilite-resume-engagements">
            {basis.commitments.map((one) => (
              <li key={one.id}>
                {one.lenderName} : {formatMoney(one.monthlyInstalment)}{" "}
                {t("client.eligibilite.resumeParMois")}
              </li>
            ))}
          </ul>
        </section>
      )}

      {stale ? (
        <p
          data-testid="eligibilite-solde-ancien"
          role="status"
          className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700"
        >
          {t("client.eligibilite.soldeAncien")
            .replace("{jours}", String(basis.staleAfterDays))
            .replace("{preteurs}", staleLenders)}
        </p>
      ) : null}

      {basis.ready && basis.lenders.length > 0 ? (
        <p data-testid="eligibilite-banques" className="text-xs leading-relaxed text-gray-500">
          {t("client.eligibilite.banquesNote").replace("{preteurs}", naming(basis.lenders))}
        </p>
      ) : null}

      <p
        data-testid="eligibilite-source"
        className="rounded-xl bg-gray-50 px-3 py-2.5 text-xs leading-relaxed text-gray-500"
      >
        {t("client.eligibilite.source")}
      </p>

      {props.refusal === null ? null : (
        <p role="alert" data-testid="eligibilite-refus" className="text-sm text-rose-600">
          {props.refusal}
        </p>
      )}

      {basis.ready ? (
        <button
          type="button"
          data-testid="eligibilite-verifier"
          disabled={props.busy}
          onClick={() => {
            props.onSubmit();
          }}
          className="w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-40"
        >
          {t("client.eligibilite.verifier")}
        </button>
      ) : (
        <button
          type="button"
          data-testid="eligibilite-completer"
          onClick={props.onEditProfile}
          className="w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white"
        >
          {t("client.eligibilite.completer")}
        </button>
      )}
      <button
        type="button"
        data-testid="eligibilite-modifier-profil"
        onClick={props.onEditProfile}
        className="w-full rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-500"
      >
        {t("client.eligibilite.modifierProfil")}
      </button>
      <p className="text-center text-xs leading-relaxed text-gray-400">{t("client.eligibilite.gratuit")}</p>
    </div>
  );
}
